import React, { ReactElement, useState } from 'react';
import SidebarIcons from './SidebarIcons';
import { TAB_ANALYTICS, TAB_DEVICES, TAB_RAW_DATA } from '../pages/data.page';
import SidebarSubIcons from './SideBarSubIcons';
import { getUserNameFromSessionStorage } from '../services/session-storage-service';

const ADMIN_EMAIL = 'suresh@potsdamsensors.com';

export type IconProps = {
  id: number | string;
  iconName: string;
  iconText: string;
  route: string;
};

export type SubIconProps = {
  id: number | string;
  iconName: string;
  iconText: string;
  /** Tab of the parent page. Mutually exclusive with `route`. */
  tab?: string;
  /** A page of its own, grouped under the parent. Mutually exclusive with `tab`. */
  route?: string;
};

// eslint-disable-next-line jsdoc/require-jsdoc
interface IconAttribute extends IconProps {
  subRoutes?: SubIconProps[];
}

const DATA_PAGE_TABS = [
  { id: TAB_RAW_DATA, iconName: 'scatter_plot', iconText: 'Raw Data', tab: 'raw-data' },
  { id: TAB_ANALYTICS, iconName: 'query_stats', iconText: 'Analytics', tab: 'analytics' },
  { id: TAB_DEVICES, iconName: 'memory', iconText: 'Devices', tab: 'devices' }
];

const BASE_ICONS: IconAttribute[] = [
  { id: 1, iconName: 'home', iconText: 'Dashboard', route: '' },
  { id: 2, iconName: 'notifications', iconText: 'Activity', route: 'activity' },
  {
    id: 3,
    iconName: 'equalizer',
    iconText: 'Data',
    route: 'data',
    subRoutes: DATA_PAGE_TABS
  },
  { id: 4, iconName: 'description', iconText: 'Reports', route: 'reports' },
  { id: 5, iconName: 'troubleshoot', iconText: 'Insights', route: 'insights' },
  { id: 9, iconName: 'auto_awesome', iconText: 'AI Agent', route: 'ai-agent' },
  { id: 6, iconName: 'receipt_long', iconText: 'Billing', route: 'billing/history' },
  { id: 7, iconName: 'settings', iconText: 'Settings', route: 'settings' }
];

/**
 * Particle-size analysis pages. They are their own routes rather than tabs of the
 * data page, but they belong with the data views, so they hang off the Data entry.
 */
const PARTICLE_SUB_ROUTES: SubIconProps[] = [
  { id: 'opc-bins', iconName: 'grain', iconText: 'OPC-N3 Bins', route: 'opc-bins' },
  { id: 'aps-channels', iconName: 'blur_on', iconText: 'APS Channels', route: 'aps-channels' }
];

/**
 * Build the sidebar icon list, appending the admin-only entries when appropriate.
 * @returns Ordered list of sidebar icons for the current user.
 */
function buildIcons(): IconAttribute[] {
  const isAdmin =
    getUserNameFromSessionStorage().toLowerCase() === ADMIN_EMAIL.toLowerCase();
  // The particle pages are nested under Data, so this rewrites that entry's
  // sub-routes rather than adding two more top-level icons. Everyone sees them
  // as of 2026-09-24; each page handles devices that have no such sensor.
  const icons = BASE_ICONS.map((icon) =>
    icon.route === 'data'
      ? { ...icon, subRoutes: [...(icon.subRoutes ?? []), ...PARTICLE_SUB_ROUTES] }
      : icon
  );
  if (!isAdmin) return icons;
  return [
    ...icons,
    { id: 12, iconName: 'dashboard', iconText: 'Overview (Admin)', route: 'admin/overview' },
    { id: 8, iconName: 'payments', iconText: 'Payments (Admin)', route: 'admin/payments' },
    {
      id: 10,
      iconName: 'forum',
      iconText: 'AI Sessions (Admin)',
      route: 'admin/ai-sessions'
    },
    {
      id: 11,
      iconName: 'bluetooth_searching',
      iconText: 'BLE Pings (Admin)',
      route: 'admin/ble-pings'
    }
  ];
}

const bottomIcons = [
  { id: 1, iconName: 'help_outline', iconText: 'Support', route: 'support' },
  { id: 2, iconName: 'question_answer', iconText: 'Help Center', route: 'help-center' }
];

/**
 * Component for sidebar.
 * @param props - Properties.
 * @param props.currentIcon - Currenticon.
 * @returns - Side bar component.
 */
function SideBarIconComponent(props: { currentIcon: IconAttribute }): ReactElement {
  const { currentIcon } = props;
  const [subRouteVisible, setSubRouteVisible] = useState(true);
  return (
    <React.Fragment key={currentIcon.id}>
      <SidebarIcons
        onClickSubRouteExpand={() => setSubRouteVisible((prev) => !prev)}
        icondetails={currentIcon}
        subRouteVisible={subRouteVisible}
      />
      {subRouteVisible &&
        currentIcon.subRoutes &&
        currentIcon.subRoutes.map((item) => (
          <div
            className="pl-3 hidden group-hover/sidenav:block dark:hover:group-hover/sidenav:bg-background-500"
            key={item.id}
          >
            <SidebarSubIcons parentRoute={currentIcon.route} icondetails={item} />
          </div>
        ))}
    </React.Fragment>
  );
}

/**
 * Leftbar component displaying navigation icons and buttons.
 * @returns  A React element representing the Leftbar component.
 */
export default function SideNavBar(): ReactElement {
  return (
    /* TODO: have to resolve fixed width things remove w-52 class */
    <div className="flex flex-col w-12 sm:w-16 fixed top-0 border-r-gray-100 border-r bg-primary-100 justify-between dark:bg-background-700 dark:border-r-gray-800 hover:w-56 px-2 sm:px-4 group/sidenav z-50 min-h-screen bottom-0">
      <img
        src={`${process.env.PUBLIC_URL}/logo.png`}
        alt="Telos Air LOGO"
        className="md:mt-4 w-16 group-hover/sidenav:hidden mt-safe" // Hide the default image on hover
      />
      <img
        src={`${process.env.PUBLIC_URL}/assets/telosair_white.png`} // Hover image
        alt="Telos Air LOGO Hover"
        className="mt-4 w-36 hidden group-hover/sidenav:block object-contain" // Show only on hover
      />
      <div className="py-3 grow">
        {buildIcons().map((currentIcon) => {
          return <SideBarIconComponent key={currentIcon.id} currentIcon={currentIcon} />;
        })}
      </div>
      <div className="py-5">
        <div className="bg-white p-4 rounded mb-2 dark:bg-container-500 invisible group-hover/sidenav:visible">
          <div className="flex justify-between mb-1">
            <p className="text-sm font-medium text-gray-900 dark:text-white">Start a Tour</p>
            <span className="material-icons-outlined dark:text-gray-500 cursor-pointer">close</span>
          </div>
          <p className="text-gray-400 font-normal text-sm dark:text-gray-500">Explore Dashboard</p>
        </div>
        {bottomIcons.map((currentIcon) => (
          <SidebarIcons key={currentIcon.id} icondetails={currentIcon} />
        ))}
      </div>
    </div>
  );
}
